// customHooks/useUpdateBookingDates.ts
import { useCallback } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { useRecoilState } from 'recoil'

import { UPDATE_BOOKING_BY_BOOKING_ID } from 'graphql/service'
import { useGlobalAlerts } from 'customHooks/useGlobalAlerts'
import { reloadTrips } from 'states/CommonStates/atoms'

type UpdateBookingDatesInput = {
  checkInDate: string
  checkOutDate: string
}

type UpdateBookingDatesResult = {
  updateBooking: {
    id: string
  }
}

type UpdateBookingDatesVars = {
  bookingId: string
  input: UpdateBookingDatesInput
}

type UpdateManyResult = {
  successIds: string[]
  failed: Array<{ bookingId: string; error: ApolloError | Error }>
}

type HookOptions = {
  /**
   * Callback fired after successful updates.
   * Receives a list of successfully updated booking IDs.
   */
  onCompleted?: (updatedBookingIds: string[]) => void

  /**
   * Callback fired on error.
   */
  onError?: (error: ApolloError | Error) => void

  /**
   * If true, group update stops on the first error.
   * If false, updates all bookings and collects errors.
   */
  failFast?: boolean

  /**
   * Allows disabling global alerts if silent behavior is required.
   */
  showAlerts?: boolean

  /**
   * If true (default), toggles reloadTrips atom after successful updates
   * to force trips refetch in useLoadTrips().
   */
  triggerReloadTrips?: boolean
}

/**
 * Converts Apollo / JS errors into a human-readable message.
 */
const getHumanErrorMessage = (e: ApolloError | Error) => {
  if ('graphQLErrors' in e && e.graphQLErrors?.length) {
    return e.graphQLErrors.map((x) => x.message).filter(Boolean).join('\n')
  }

  if ('networkError' in e && e.networkError) {
    return `Network error: ${String((e.networkError as any)?.message || e.networkError)}`
  }

  return e.message || 'Unknown error'
}

export const useUpdateBookingDates = (options?: HookOptions) => {
  const {
    onCompleted,
    onError,
    failFast = false,
    showAlerts = true,
    triggerReloadTrips = true,
  } = options || {}

  const { showGlobalError, showGlobalMessage } = useGlobalAlerts()

  const [reload, setReload] = useRecoilState(reloadTrips)

  const [mutate, { loading, error }] = useMutation<
    UpdateBookingDatesResult,
    UpdateBookingDatesVars
  >(UPDATE_BOOKING_BY_BOOKING_ID)

  /**
   * Forces trips reload via Recoil atom toggle.
   */
  const forceReloadTrips = useCallback(
    (updatedCount: number) => {
      if (!triggerReloadTrips) return
      if (updatedCount <= 0) return
      setReload(!reload)
    },
    [triggerReloadTrips, reload, setReload]
  )

  /**
   * Updates dates for a single booking.
   */
  const updateDateForSingleBooking = useCallback(
    async (bookingId: string, input: UpdateBookingDatesInput) => {
      try {
        const res = await mutate({ variables: { bookingId, input } })
        const updatedId = res.data?.updateBooking?.id || bookingId

        if (showAlerts) {
          showGlobalMessage?.('Booking dates updated.')
        }

        // Force trips reload after successful update
        forceReloadTrips(1)

        onCompleted?.([updatedId])
        return updatedId
      } catch (e) {
        const err = e as ApolloError | Error

        if (showAlerts) {
          showGlobalError(getHumanErrorMessage(err))
        }

        onError?.(err)
        throw err
      }
    },
    [
      mutate,
      onCompleted,
      onError,
      showAlerts,
      showGlobalError,
      showGlobalMessage,
      forceReloadTrips,
    ]
  )

  /**
   * Updates dates for multiple bookings (group booking).
   */
  const updateDateForGroupBooking = useCallback(
    async (bookingIds: string[], input: UpdateBookingDatesInput): Promise<UpdateManyResult> => {
      if (!bookingIds.length) {
        onCompleted?.([])
        return { successIds: [], failed: [] }
      }

      // failFast === true: stop execution on the first error
      if (failFast) {
        try {
          const successIds = await Promise.all(
            bookingIds.map((bookingId) =>
              mutate({ variables: { bookingId, input } }).then(() => bookingId)
            )
          )

          if (showAlerts) {
            showGlobalMessage?.('Group booking dates updated.')
          }

          // Force trips reload after successful updates
          forceReloadTrips(successIds.length)

          onCompleted?.(successIds)
          return { successIds, failed: [] }
        } catch (e) {
          const err = e as ApolloError | Error

          if (showAlerts) {
            showGlobalError(getHumanErrorMessage(err))
          }

          onError?.(err)
          throw err
        }
      }

      // failFast === false: update all bookings and collect results
      const results = await Promise.allSettled(
        bookingIds.map((bookingId) =>
          mutate({ variables: { bookingId, input } }).then(() => bookingId)
        )
      )

      const successIds: string[] = []
      const failed: UpdateManyResult['failed'] = []

      results.forEach((r, idx) => {
        const bookingId = bookingIds[idx]
        if (r.status === 'fulfilled') {
          successIds.push(r.value)
        } else {
          failed.push({
            bookingId,
            error: (r.reason ?? new Error('Unknown error')) as ApolloError | Error,
          })
        }
      })

      // Show aggregated result in global alerts
      if (showAlerts) {
        if (failed.length === 0) {
          showGlobalMessage?.('Group booking dates updated.')
        } else if (successIds.length === 0) {
          showGlobalError('Failed to update booking dates for the whole group.')
        } else {
          showGlobalError(
            `Group booking dates partially updated: ${successIds.length} succeeded, ${failed.length} failed.`
          )
        }
      }

      // Force trips reload if at least one booking was updated
      forceReloadTrips(successIds.length)

      if (failed.length) {
        onError?.(failed[0].error)
      }

      onCompleted?.(successIds)
      return { successIds, failed }
    },
    [
      mutate,
      failFast,
      onCompleted,
      onError,
      showAlerts,
      showGlobalError,
      showGlobalMessage,
      forceReloadTrips,
    ]
  )

  return {
    updateDateForSingleBooking,
    updateDateForGroupBooking,
    loading,
    error,
  }
}